import { FormNotice, theme } from '@zappyrent/hundop';
import { debounce, upperFirst } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';

import { useToastContext } from '../../../contexts/ToastProvider';
import userAPI from '../../../services/api/user';
import { IUserResetPasswordForm } from '../../../shared/ts/user';
import useStore from '../../../store';
import TextInput from '../../___design/TextInput';
import Modal from '../../Modal/Modal';
import ModalBody from '../../Modal/ModalBody/ModalBody';
import ModalContent from '../../Modal/ModalContent/ModalContent';
import ModalFooter from '../../Modal/ModalFooter/ModalFooter';
import ModalHeader from '../../Modal/ModalHeader/ModalHeader';
import {
  StyleButtonWrapper,
  StyledButton,
  StyledSpinner,
} from './UserResetPasswordModal.styles';

const UserResetPasswordModal = ({ onClose }: { onClose: () => void }) => {
  const toast = useToastContext();
  const { t } = useTranslation(['common', 'formError', 'translation', 'error']);
  const defaultFormErrorLabel = t('form:requiredField');
  const { user } = useStore(state => ({
    user: state.user,
  }));

  const { mutateAsync, isLoading } = useMutation(userAPI.updateUserPassword, {
    onSettled(data) {
      if (data?.status === 400) {
        toast.error(upperFirst(data.message));
      }
      if (data?.status === 200) {
        toast.success(upperFirst(t('translation:resetPasswordSuccess')));
        onClose();
      }
    },
  });

  const handleFormSubmit = async ({
    old_password,
    new_password,
  }: IUserResetPasswordForm) => {
    try {
      mutateAsync({
        new_password,
        old_password,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const { control, formState, handleSubmit, watch } =
    useForm<IUserResetPasswordForm>();

  return (
    <Modal>
      <ModalBody>
        <ModalHeader
          title={upperFirst(t('translation:resetPassword'))}
          onClose={onClose}
        />
        <form
          id="booking_counter_offer_form"
          onSubmit={handleSubmit(handleFormSubmit)}
          noValidate
        >
          <ModalContent>
            <Controller
              control={control}
              name="old_password"
              rules={{
                required: defaultFormErrorLabel,
              }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextInput
                  id="user-old-password"
                  label={upperFirst(t('form:oldPassword'))}
                  name="oldPassword"
                  type="text"
                  value={value}
                  onChange={debounce(onChange, 300)}
                >
                  {formState.errors.old_password && (
                    <FormNotice isWrapped={true}>{error?.message}</FormNotice>
                  )}
                </TextInput>
              )}
            />
            <Controller
              control={control}
              name="new_password"
              rules={{
                required: defaultFormErrorLabel,
              }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextInput
                  id="user-new-password"
                  label={upperFirst(t('form:newPassword'))}
                  name="new_password"
                  value={value}
                  onChange={debounce(onChange, 300)}
                >
                  {formState.errors.new_password && (
                    <FormNotice isWrapped={true}>{error?.message}</FormNotice>
                  )}
                </TextInput>
              )}
            />
            <Controller
              control={control}
              name="confirm_password"
              rules={{
                required: defaultFormErrorLabel,
                validate: (val: string) => {
                  if (watch('new_password') != val) {
                    return upperFirst(t('formError:passwordMatch'));
                  }
                },
              }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextInput
                  id="user-confirm-password"
                  label={upperFirst(t('form:confirmPassword'))}
                  name="confirm_password"
                  type="text"
                  value={value}
                  onChange={debounce(onChange, 300)}
                >
                  {formState.errors.confirm_password && (
                    <FormNotice isWrapped={true}>{error?.message}</FormNotice>
                  )}
                </TextInput>
              )}
            />
          </ModalContent>
          <ModalFooter>
            <StyleButtonWrapper>
              <StyledButton
                user={user?.type}
                variant="secondary"
                onClick={onClose}
              >
                {upperFirst(t('common:cancel'))}
              </StyledButton>
              <StyledButton
                user={user?.type}
                type="submit"
                disabled={isLoading}
              >
                {isLoading && (
                  <StyledSpinner
                    foregroundColor={theme.colors.cross.ultralightGray}
                    size={15}
                    strokeSize={2}
                  />
                )}
                {upperFirst(t('common:update'))}
              </StyledButton>
            </StyleButtonWrapper>
          </ModalFooter>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default UserResetPasswordModal;
